import React from 'react';
import { Helmet } from 'react-helmet-async';
import './Privacy.css';

function Privacy() {
  return (
    <div className="privacy-page">
      <Helmet>
        <title>Privacy Policy - Audra Books</title>
        <meta name="description" content="Privacy Policy for Audra Books. Learn how we protect your data with our privacy-first approach. No data collection, direct server connection, full user control." />
        <link rel="canonical" href="https://audrabooks.app/privacy" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="container">
        <div className="privacy-content">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: January 9, 2026</p>

          <section className="privacy-highlight">
            <h2>Our Privacy Commitment</h2>
            <p>
              Audra Books is built with privacy as a core principle. All data flows directly between
              your device and your Audiobookshelf server. We never see, store, or process your content
              or personal information.
            </p>
            <p>
              <strong>Exception:</strong> Audra Pro includes an optional AI-powered author biography
              feature. When enabled, limited data (author names and book titles) is sent to our proxy
              server for processing. This feature is disabled by default and requires explicit consent.
              See "AI-Powered Features" below for details.
            </p>
          </section>

          <section>
            <h2>Information We Do NOT Collect</h2>
            <p>We do not collect, store, or have access to:</p>
            <ul>
              <li>Your Audiobookshelf server credentials or URLs</li>
              <li>Your audiobook or ebook library contents</li>
              <li>Your listening or reading history</li>
              <li>Your bookmarks, highlights, or annotations</li>
              <li>Your personal statistics or listening patterns</li>
              <li>Any content transmitted between your device and server</li>
            </ul>
          </section>

          <section>
            <h2>Data Stored on Your Device</h2>
            <p>All app data is stored locally on your device:</p>
            <ul>
              <li>
                <strong>Server Credentials:</strong> Your server URL and login credentials are
                stored securely using platform-native encryption (iOS Keychain or Android
                EncryptedSharedPreferences).
              </li>
              <li>
                <strong>Downloaded Content:</strong> Audiobooks and ebooks you download for
                offline use are stored in the app's private storage area.
              </li>
              <li>
                <strong>App Preferences:</strong> Your theme selections, playback settings, and
                other preferences are stored locally.
              </li>
              <li>
                <strong>Cached Data:</strong> Temporary data like cover images and metadata may
                be cached to improve performance.
              </li>
            </ul>
          </section>

          <section>
            <h2>Platform-Specific Features</h2>

            <h3>iOS</h3>
            <p>
              You may optionally enable iCloud sync for ebook bookmarks and highlights. When enabled,
              this data is synchronized through Apple's iCloud and governed by Apple's Privacy Policy.
              We do not have access to iCloud data.
            </p>

            <h3>Android</h3>
            <p>
              All data remains on your device. Credentials are encrypted using Android's
              EncryptedSharedPreferences with hardware-backed security when available.
            </p>
          </section>

          <section>
            <h2>AI-Powered Features (Pro Subscription)</h2>
            <p>
              Audra Pro includes an optional AI-powered author biography feature. This feature is:
            </p>
            <ul>
              <li><strong>Disabled by default</strong> - You must explicitly enable it in Settings</li>
              <li><strong>Pro-only</strong> - Only available to Audra Pro subscribers</li>
              <li><strong>Opt-in</strong> - Requires your consent before activation</li>
            </ul>

            <h3>What Data Is Sent</h3>
            <p>
              Data is only sent when you navigate to an author page. When you enable AI author
              biographies and view an author page, the following data is sent:
            </p>
            <ul>
              <li>
                <strong>To Our Proxy Server:</strong> Author name, book titles from that author,
                your IP address (standard for any connection), and app bundle identifier
                (for abuse prevention)
              </li>
              <li>
                <strong>To OpenAI:</strong> Only the author name and book titles. Your IP address
                and app identifier are NOT forwarded to OpenAI.
              </li>
            </ul>
            <p>
              <strong>We do NOT send:</strong> Your server credentials, reading history, personal
              information, or any other library data beyond the author name and book titles.
            </p>

            <h3>How It Works</h3>
            <ol>
              <li>
                <strong>Your Device:</strong> Sends author name and book titles to our Cloudflare
                proxy server
              </li>
              <li>
                <strong>Our Proxy:</strong> Checks server-side cache (30-day retention). If cached,
                returns immediately. If not cached, forwards request to OpenAI
              </li>
              <li>
                <strong>OpenAI API:</strong> Generates biography content based on the author
                information provided
              </li>
              <li>
                <strong>Response:</strong> Biography is sent back to your device and cached on
                our server for 30 days
              </li>
            </ol>

            <h3>Data Retention and Storage</h3>
            <ul>
              <li>
                <strong>Your Device:</strong> Biographies are cached locally for 30 days, after
                which they are automatically deleted
              </li>
              <li>
                <strong>Our Servers:</strong> Biographies are cached for 30 days to reduce costs
                and improve performance. After 30 days, cached data is automatically deleted. We
                do not permanently store this data.
              </li>
              <li>
                <strong>OpenAI:</strong> Data sent to OpenAI is subject to their{' '}
                <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">
                  privacy policy
                </a> and{' '}
                <a href="https://openai.com/policies/api-data-usage-policies" target="_blank" rel="noopener noreferrer">
                  data usage policies
                </a>. OpenAI states they do not use API data to train their models.
              </li>
            </ul>

            <h3>How to Control This Feature</h3>
            <ul>
              <li>
                <strong>Enable:</strong> Go to Settings → AI Features → Toggle "AI Author
                Biographies" ON
              </li>
              <li>
                <strong>Disable:</strong> Toggle OFF at any time to stop sending data
              </li>
              <li>
                <strong>Clear Cache:</strong> Clear app cache to remove locally stored biographies
              </li>
            </ul>

            <h3>Security and Privacy Protections</h3>
            <ul>
              <li>
                <strong>Rate Limiting:</strong> Abuse prevention measures are in place to protect
                against misuse
              </li>
              <li>
                <strong>Secure Transit:</strong> All connections use HTTPS encryption
              </li>
              <li>
                <strong>Proxy Server:</strong> Your requests go through our secure proxy server,
                not directly to OpenAI
              </li>
            </ul>

            <p>
              <strong>Important:</strong> This feature is completely optional. If you choose not
              to enable it, no author or book information will ever be sent to our servers or
              OpenAI.
            </p>
          </section>

          <section>
            <h2>Third-Party Services</h2>
            <p>
              Audra Books connects to the following third-party services:
            </p>
            <ul>
              <li>
                <strong>Your Audiobookshelf Server:</strong> The app connects directly to your
                self-hosted or managed Audiobookshelf server. Your server operator's privacy
                practices govern data on their server.
              </li>
              <li>
                <strong>App Store / Google Play:</strong> Subscription purchases are processed
                through Apple or Google's payment systems and are subject to their respective
                privacy policies.
              </li>
              <li>
                <strong>Apple iCloud (iOS only):</strong> If you enable iCloud sync, Apple's
                privacy policy applies to synced data.
              </li>
              <li>
                <strong>OpenAI (Pro users with AI features enabled):</strong> If you enable AI
                author biographies, data is processed through OpenAI's API. See the "AI-Powered
                Features" section above for details.
              </li>
            </ul>
          </section>

          <section>
            <h2>Analytics and Diagnostics</h2>
            <p>
              Audra Books does not include third-party analytics or tracking. We do not collect
              usage analytics, crash reports, or behavioral data.
            </p>
            <p>
              If you share crash reports through your device's operating system, those reports are
              handled by Apple or Google according to their privacy policies.
            </p>
          </section>

          <section>
            <h2>Security Measures</h2>
            <ul>
              <li>
                <strong>Encrypted Credential Storage:</strong> Server credentials use platform-native
                secure storage (iOS Keychain, Android EncryptedSharedPreferences).
              </li>
              <li>
                <strong>Secure Communications:</strong> HTTPS connections encrypt data transmission
                to your server.
              </li>
              <li>
                <strong>No Remote Access:</strong> We cannot remotely access data on your device.
              </li>
              <li>
                <strong>Biometric Protection:</strong> Your device's passcode, Face ID, or fingerprint
                provides additional security.
              </li>
            </ul>
          </section>

          <section>
            <h2>Your Rights and Controls</h2>
            <p>You have complete control over your data in Audra Books:</p>
            <ul>
              <li>
                <strong>Delete Credentials:</strong> Remove all stored server connections and
                credentials from within the app.
              </li>
              <li>
                <strong>Clear Downloads:</strong> Delete all downloaded audiobooks and ebooks
                at any time.
              </li>
              <li>
                <strong>Clear Cache:</strong> Remove all cached data including cover images,
                metadata, and AI-generated biographies.
              </li>
              <li>
                <strong>Control AI Features (Pro):</strong> Enable or disable AI author biographies
                at any time through Settings → AI Features.
              </li>
              <li>
                <strong>Manage iCloud Sync (iOS):</strong> Enable or disable iCloud
                synchronization through the app settings.
              </li>
              <li>
                <strong>Uninstall:</strong> Removing the app will delete all locally stored
                data.
              </li>
            </ul>

            <h3>Data Subject Rights (GDPR)</h3>
            <p>
              For users in the European Union, you have additional rights under GDPR:
            </p>
            <ul>
              <li>
                <strong>Right to Access:</strong> Request information about what data we process.
                For AI biography data, this includes author names and book titles you've viewed.
              </li>
              <li>
                <strong>Right to Deletion:</strong> Request deletion of cached biography data from
                our servers. Note that data automatically expires after 30 days.
              </li>
              <li>
                <strong>Right to Object:</strong> Opt out of AI biography processing at any time by
                disabling the feature in Settings.
              </li>
              <li>
                <strong>Right to Data Portability:</strong> Due to the minimal, non-personal nature
                of the data, portability is limited. Locally cached data is accessible through your
                device's file system.
              </li>
            </ul>
            <p>
              To exercise these rights, contact us at{' '}
              <a href="mailto:hello@audrabooks.app">hello@audrabooks.app</a>.
            </p>
          </section>

          <section>
            <h2>Children's Privacy</h2>
            <p>
              Audra Books is not directed at children under 13. We do not knowingly collect personal
              information from children. If you believe a child has provided personal information,
              please contact us.
            </p>
          </section>

          <section>
            <h2>International Users</h2>
            <ul>
              <li>
                <strong>GDPR (European Union):</strong> Our minimal data collection aligns with GDPR
                principles. For AI biographies, we collect only necessary data (author names and book
                titles), provide clear consent, and automatically delete cached data after 30 days.
                See "Your Rights and Controls" for GDPR-specific rights.
              </li>
              <li>
                <strong>CCPA (California):</strong> We do not sell personal information. Data collected
                for AI biographies is used solely for providing the service and is not shared except as
                necessary for operation (OpenAI API processing).
              </li>
            </ul>
            <p>
              Most data remains on your device and server, giving you full control regardless of location.
            </p>
          </section>

          <section>
            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Significant changes will be
              reflected in the "Last Updated" date above. Please review this policy periodically.
            </p>
          </section>

          <section>
            <h2>Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy, please contact us at{' '}
              <a href="mailto:hello@audrabooks.app">hello@audrabooks.app</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Privacy;
